// src/lib/ttsClient.ts
// Updated: 2026-08-04 18:48:00 CDT
// Purpose: Authenticated client for the TableRead.tv Supabase TTS proxy.

import { supabase } from "@/lib/supabaseClient";

export type Health = {
  status: string;
  service: string;
  version: string;
  model_loaded: boolean;
  model_loading: boolean;
  load_error: boolean;
  active: number;
  waiting: number;
  limits: {
    max_concurrency: number;
    max_queued: number;
  };
};

export type HealthResponseMetadata={http_status:number;request_id?:string;function_version?:string;cf_ray?:string;body_bytes?:number;body_sha256?:string;error_code?:string};
const healthResponseMetadata=(response:Response):HealthResponseMetadata=>{
 const result:HealthResponseMetadata={http_status:response.status};
 for(const [key,header] of [['request_id','x-request-id'],['function_version','x-tableread-function-version'],['cf_ray','cf-ray']] as const){
  const value=response.headers.get(header);if(value&&/^[a-zA-Z0-9._:-]{1,100}$/.test(value))result[key]=value;
 }return result;
};
export class TtsHealthError extends Error{
 constructor(readonly diagnostic:HealthResponseMetadata){super(`Support health check returned HTTP ${diagnostic.http_status}.`);this.name='TtsHealthError';}
}

export type Stats = {
  service: string;
  version: string;
  model: {
    name: string;
    loaded: boolean;
    loading: boolean;
    loaded_at_epoch: number | null;
    load_seconds: number | null;
    checkpoint: string;
    checkpoint_sha256: string;
    config: string;
    vocabulary: string;
    vocoder: string;
  };
  cuda: {
    available: boolean;
    pytorch: string;
    cuda_build: string;
    device: string;
    compute_capability: number[];
    vram_bytes: number;
  };
  queue: {
    active: number;
    waiting: number;
    max_concurrency: number;
    max_queued: number;
  };
  generation_defaults: {
    target_rms: number;
    cross_fade_duration: number;
    nfe_step: number;
    cfg_strength: number;
    sway_sampling_coefficient: number;
    speed: number;
  };
};

export type SynthesizeRequest = {
  provider: string;
  text: string;
  ref_audio: string | null;
  ref_text: string;
  language: string;
  style_tags: string[];
  speed?: number;
  seed?: number;
};

export type SynthesizeResult = {
  blob: Blob;
  elapsedMs: number;
  contentType: string;
  contentLength?: number;
  generationId?: string;
  generationTime?: number;
  sampleRate?: number;
};

type ClientOptions = {
  baseUrl: string;
  apiKey?: string;

  /*
   * Retained for compatibility with the existing Diagnostics interface.
   * If supplied, this must be a user access token—not the anonymous key.
   * The active Supabase session token takes priority.
   */
  supabaseKey?: string;

  verbose?: boolean;
};

function redactHeaders(headers: Headers): Record<string, string> {
  const safeHeaders: Record<string, string> = {};

  headers.forEach((value, key) => {
    const normalizedKey = key.toLowerCase();

    if (
      normalizedKey === "authorization" ||
      normalizedKey === "apikey" ||
      normalizedKey === "x-api-key"
    ) {
      safeHeaders[key] = "[REDACTED]";
    } else {
      safeHeaders[key] = value;
    }
  });

  return safeHeaders;
}

function parseOptionalInteger(value: string | null): number | undefined {
  if (!value) {
    return undefined;
  }

  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function parseOptionalNumber(value: string | null): number | undefined {
  if (!value) {
    return undefined;
  }

  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

export function createTTSClientFromEnv(opts?: Partial<ClientOptions>) {
  /*
   * Vite statically replaces direct import.meta.env property access during
   * the production build. Keep these references direct rather than copying
   * import.meta.env into another object.
   */
  const configuredBaseUrl =
    import.meta.env.VITE_TTS_BASE_URL ||
    import.meta.env.NEXT_PUBLIC_TTS_BASE_URL ||
    "";

  const configuredSupabaseUrl =
    import.meta.env.VITE_SUPABASE_URL ||
    import.meta.env.NEXT_PUBLIC_SUPABASE_URL ||
    "";

  const defaultProxyUrl = configuredSupabaseUrl
    ? `${String(configuredSupabaseUrl).replace(/\/+$/, "")}/functions/v1/tts-proxy`
    : "";

  const baseUrl = String(
    opts?.baseUrl || configuredBaseUrl || defaultProxyUrl,
  ).replace(
    /\/+$/,
    ""
  );

  /*
   * The publishable key identifies the Supabase project. It is not used as
   * the user's Bearer token.
   */
  const publishableKey = String(
    import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY ||
      import.meta.env.VITE_SUPABASE_ANON_KEY ||
      import.meta.env.NEXT_PUBLIC_SUPABASE_ANON_KEY ||
      ""
  );

  /*
   * This remains available for manual Diagnostics testing, but the normal
   * application path uses the signed-in user's current session token.
   */
  const manualAccessToken = String(opts?.supabaseKey || "");

  /*
   * This is only retained for explicit direct testing. The production
   * Supabase proxy supplies the private RunPod API key server-side.
   */
  const apiKey = String(opts?.apiKey || "");

  const verbose = opts?.verbose ?? false;

  async function resolveAccessToken(): Promise<string> {
    const {
      data: { session },
      error,
    } = await supabase.auth.getSession();

    if (error) {
      console.error("[ttsClient] Unable to read Supabase session:", error);
      throw new Error(`Unable to read the Supabase session: ${error.message}`);
    }

    if (session?.access_token) {
      if (verbose) {
        console.debug("[ttsClient] Using active Supabase user session.");
      }

      return session.access_token;
    }

    if (manualAccessToken) {
      if (verbose) {
        console.debug("[ttsClient] Using manually supplied access token.");
      }

      return manualAccessToken;
    }

    return "";
  }

  async function doFetch(
    path: string,
    init?: RequestInit
  ): Promise<Response> {
    if (!baseUrl) {
      throw new Error(
        "TTS base URL is not configured. Set VITE_TTS_BASE_URL."
      );
    }

    const normalizedPath = path.startsWith("/") ? path : `/${path}`;
    const routePath = normalizedPath.split("?")[0];
    const requiresAuthentication = routePath !== "/health";
    const url = `${baseUrl}${normalizedPath}`;

    const accessToken = requiresAuthentication ? await resolveAccessToken() : "";

    if (requiresAuthentication && !accessToken) {
      throw new Error(
        "You must be signed in before using this TTS endpoint."
      );
    }

    const headers = new Headers(init?.headers);

    if (init?.body && !headers.has("Content-Type")) {
      headers.set("Content-Type", "application/json");
    }

    if (publishableKey) {
      headers.set("apikey", publishableKey);
    }

    if (accessToken) {
      headers.set("Authorization", `Bearer ${accessToken}`);
    }

    if (apiKey) {
      headers.set("X-Api-Key", apiKey);
    }

    const method = init?.method || "GET";

    if (verbose) {
      console.debug("[ttsClient] Request:", {
        method,
        url,
        headers: redactHeaders(headers),
        body:
          typeof init?.body === "string"
            ? init.body
            : init?.body
              ? "[NON-TEXT BODY]"
              : undefined,
      });
    }

    let response: Response;

    try {
      response = await fetch(url, {
        ...init,
        headers,
      });
    } catch (error) {
      console.error("[ttsClient] Network request failed:", {
        method,
        url,
        error,
      });

      throw error;
    }

    if (verbose) {
      console.debug("[ttsClient] Response:", {
        method,
        url,
        status: response.status,
        statusText: response.statusText,
        headers: redactHeaders(response.headers),
      });
    }

    if (!response.ok) {
      if(routePath==='/health'){
        const diagnostic=healthResponseMetadata(response);
        try{
          const body=await response.text();diagnostic.body_bytes=new TextEncoder().encode(body).length;
          diagnostic.body_sha256=[...new Uint8Array(await crypto.subtle.digest('SHA-256',new TextEncoder().encode(body)))].map(n=>n.toString(16).padStart(2,'0')).join('');
          const payload=JSON.parse(body);const code=payload?.code??payload?.error;
          if(['TTS upstream request timed out','TTS proxy request failed','Authentication required'].includes(code))diagnostic.error_code=code.replaceAll(' ','_').toUpperCase();
        }catch{/* The original HTTP status and request IDs survive an unreadable body. */}
        throw new TtsHealthError(diagnostic);
      }
      let responseText = "";

      try {
        responseText = await response.text();
      } catch (error) {
        console.error(
          "[ttsClient] Unable to read unsuccessful response body:",
          error
        );
      }

      console.error("[ttsClient] TTS request rejected:", {
        method,
        url,
        status: response.status,
        statusText: response.statusText,
        response: responseText,
      });

      throw new Error(
        `${response.status} ${response.statusText} - ${
          responseText || "No response body"
        }`
      );
    }

    return response;
  }

  return {
    async health(options?: {
      signal?: AbortSignal;
      onResponse?: (metadata:HealthResponseMetadata)=>void;
    }): Promise<Health> {
      const response = await doFetch("/health", {
        method: "GET",
        signal: options?.signal,
      });

      options?.onResponse?.(healthResponseMetadata(response));
      return response.json();
    },

    async stats(options?: {
      signal?: AbortSignal;
    }): Promise<Stats> {
      const response = await doFetch("/stats", {
        method: "GET",
        signal: options?.signal,
      });

      return response.json();
    },

    async synthesize(
      body: SynthesizeRequest,
      options?: { signal?: AbortSignal }
    ): Promise<SynthesizeResult> {
      const started = performance.now();

      const response = await doFetch("/synthesize", {
        method: "POST",
        body: JSON.stringify(body),
        signal: options?.signal,
      });

      const blob = await response.blob();
      const elapsedMs = performance.now() - started;

      return {
        blob,
        elapsedMs,
        contentType:
          response.headers.get("content-type") ||
          "application/octet-stream",
        contentLength: parseOptionalInteger(
          response.headers.get("content-length")
        ),
        generationId:
          response.headers.get("x-generation-id") || undefined,
        generationTime: parseOptionalNumber(
          response.headers.get("x-generation-time")
        ),
        sampleRate: parseOptionalInteger(
          response.headers.get("x-sample-rate")
        ),
      };
    },
  };
}
